/* eslint-disable no-bitwise */

import {PathLayout, Position} from "../render/types.ts";
import {BoundingBox, getBoundingBox, pathLayoutArea, pathLayoutContains} from "../render/utils/mathUtils.ts";

export function cyrb128(str: string) {
  let h1 = 1779033703;
  let h2 = 3144134277;
  let h3 = 1013904242;
  let
    h4 = 2773480762;
  for (let i = 0, k; i < str.length; i += 1) {
    k = str.charCodeAt(i);
    h1 = h2 ^ Math.imul(h1 ^ k, 597399067);
    h2 = h3 ^ Math.imul(h2 ^ k, 2869860233);
    h3 = h4 ^ Math.imul(h3 ^ k, 951274213);
    h4 = h1 ^ Math.imul(h4 ^ k, 2716044179);
  }
  h1 = Math.imul(h3 ^ (h1 >>> 18), 597399067);
  h2 = Math.imul(h4 ^ (h2 >>> 22), 2869860233);
  h3 = Math.imul(h1 ^ (h3 >>> 17), 951274213);
  h4 = Math.imul(h2 ^ (h4 >>> 19), 2716044179);
  return [(h1 ^ h2 ^ h3 ^ h4) >>> 0, (h2 ^ h1) >>> 0, (h3 ^ h1) >>> 0, (h4 ^ h1) >>> 0];
}

export function sfc32(str: string) {
  let [a, b, c, d] = cyrb128(str);
  return () => {
    a >>>= 0;
    b >>>= 0;
    c >>>= 0;
    d >>>= 0;
    let t = (a + b) | 0;
    a = b ^ b >>> 9;
    b = c + (c << 3) | 0;
    c = (c << 21 | c >>> 11);
    d = d + 1 | 0;
    t = t + d | 0;
    c = c + t | 0;
    return (t >>> 0) / 4294967296;
  };
}

export function randomStr(): string {
  const str = (Math.random() + 1).toString(36).substring(7);
  if (str.length === 0) return randomStr();
  return str;
}

export function getRandomInRange(min: number, max: number): number {
  return Math.random() * (max - min) + min;
}

export function getRandomArbitrary(randomGenerator: () => number, min: number, max: number) {
  return randomGenerator() * (max - min) + min;
}

export function getRandomForGeneratorInArray<T>(randomGenerator: () => number, array: readonly T[]): T {
  const index = Math.floor(getRandomArbitrary(randomGenerator, 0, array.length));
  return array[index];
}

export function getRandomInArray<T>(array: readonly T[]): T {
  return getRandomForGeneratorInArray(Math.random, array)
}

export function getRandomPosition(boundingBox: BoundingBox, randomGenerator: () => number = Math.random): Position {
  return {
    x: getRandomArbitrary(randomGenerator, boundingBox.left.x, boundingBox.right.x),
    y: getRandomArbitrary(randomGenerator, boundingBox.bottom.y, boundingBox.top.y),
  };
}

/** Convert a layout into a grid based on the required density. Randomly place a tree within each grid cell in the layout */
export function evenlyDistributedRandomPointsInLayout(layout: PathLayout, densityPerUnitArea: number, randomGenerator: () => number = Math.random): readonly Position[] {
  const points: Position[] = [];
  const boundingBox = getBoundingBox(layout.path);

  const area = pathLayoutArea(layout)
  const numberOfPoints = Math.ceil(area * densityPerUnitArea);
  const areaPerPoint = area / numberOfPoints;

  const areaLength = Math.sqrt(areaPerPoint);

  // Walk this grid
  const widthCount = (boundingBox.right.x - boundingBox.left.x) / areaLength;
  const heightCount = (boundingBox.top.y - boundingBox.bottom.y) / areaLength;

  for (let xIndex = 0; xIndex < widthCount; xIndex++) {
    for (let yIndex = 0; yIndex < heightCount; yIndex++) {
      const position = {
        x: boundingBox.left.x + (xIndex * areaLength) + getRandomArbitrary(randomGenerator, 0, areaLength),
        y: boundingBox.bottom.y + (yIndex * areaLength) + getRandomArbitrary(randomGenerator, 0, areaLength),
      }
      if (pathLayoutContains(layout, position)) {
        points.push(position);
      }
    }
  }
  return points;
}
